import React from 'react';
import styled from 'styled-components';
import { Container, H5, Page } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import InfoIconTooltip from '@ui/InfoIconTooltip';
import { InfoIconTypes } from '@constants/infoIcons';
import { AdvancedSettingsButton } from '@containers/DigitalAnalyst/AdvancedSettings';
import { StandaloneDocSelectModal } from '@containers/docSelection/StandaloneDocSelectModal';
import ChatHistory from '@containers/DigitalAnalyst/ChatHistory';
import { useChatAskDigitalAnalyst } from '@hooks/features/useChatAskDigitalAnalyst';
import { SelectedDocuments } from '@containers/DigitalAnalyst/components/SelectedDocuments';
import { SubmitButton } from '@features/chat/components/SubmitButton';
import { useSelectedDocs } from '@hooks/state/useSelectedDocs';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';
import { AGENT_NAMES } from '@constants';

const AgentSelectWrapper = styled.div`
  width: 244px;

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }
`;
const ChatInputsWrapper = styled.div`
  width: 100%;
  gap: 8px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const FormWrapper = styled.div`
  width: 100%;

  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }

  textarea {
    border: none;
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-height: 100px;
    width: 100%;
    padding: 0 0 8px 0;
    resize: none;

    outline: none;
  }
`;
const Heading = styled.div`
  padding: 36px 0 0 0;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  h5 {
    padding-right: 8px;
  }
`;
const InputAreaWrapper = styled.div`
  margin: 0 auto 0 auto;
  max-width: 1000px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 16px;
`;

const SelectSourceDocumentsButtonWrapper = styled.div`
  button {
    padding: 12px 0 !important;
  }
`;

const RemoveAllButtonWrapper = styled.div`
  button {
    padding: 12px 0 !important;
  }
`;

export default function ChatQueryTool({
  onGoBack,
  onPageError,

  tool,
  heading,
  preSelectedDocs,

  docSelectModes,
  defaultActiveDocSelectMode,
  disableAgent,
  forceAgent,
}) {
  const [agentName, setAgentName] = React.useState(null);
  const [agents, setAgents] = React.useState([]);

  const {
    isSelectingDocs,
    selectedDocs,
    clearSelectedDocs,
    setIsSelectingDocs,
    onSelectedDocsChange,
    handleAddDocument,
    handleRemoveDocument,
  } = useSelectedDocs(preSelectedDocs);

  const [question, setQuestion] = React.useState('');
  const [isSubmittingQuestion, setIsSubmittingQuestion] = React.useState(false);

  const { onSubmit, onClearChatHistory, chatHistoryData } = useChatAskDigitalAnalyst(tool, agentName);

  React.useEffect(() => {
    const getAgentName = async () => {
      const URL = `v1/genai_agent_script_definitions`;
      const { status, data } = await sendApiRequest('get', URL);
      logNetRequest(status, data);
      handle400Statuses(status);
      if (status === 200) {
        const agents = data.map((agent) => agent.script_name);
        setAgents(agents.sort());
        setAgentName(agents.includes(AGENT_NAMES.TORNADO_CHAT_ANALYST) ? AGENT_NAMES.TORNADO_CHAT_ANALYST : agents[0]);
      }
    };
    if (forceAgent) {
      setAgentName(forceAgent);
    } else {
      if (!disableAgent) {
        getAgentName();
      }
    }
  }, []);

  const handleSubmit = async () => {
    const _question = question.trim();
    if (isSubmittingQuestion || _question.length === 0) {
      return;
    }
    setQuestion('');

    setIsSubmittingQuestion(true);
    await onSubmit({ question: _question, selectedDocs });
    setIsSubmittingQuestion(false);
  };

  const onReset = () => {
    onClearChatHistory();
    clearSelectedDocs();
    setQuestion('');
  };

  const onQuestionChange = (e) => {
    const {
      target: { value },
    } = e;
    setQuestion(value);
  };

  const handleSelectSourceDocumentsClick = () => setIsSelectingDocs(true);

  const handleDismissDocsSelection = () => setIsSelectingDocs(false);

  const onAgentChange = (e) => {
    const {
      target: { value },
    } = e;
    setAgentName(value);
  };

  return (
    <>
      {isSelectingDocs && (
        <StandaloneDocSelectModal
          docSelectModes={docSelectModes}
          defaultActiveMode={defaultActiveDocSelectMode}
          onDismiss={handleDismissDocsSelection}
          selectedDocs={selectedDocs}
          handleAddDocument={handleAddDocument}
          handleRemoveDocument={handleRemoveDocument}
          tool={tool}
        />
      )}
      <Page width={'1400px'}>
        <BackArrowButton onClick={onGoBack} />
        <Heading>
          <H5>{heading ? heading : 'Ask Digital Analyst'}</H5>
          <InfoIconTooltip type={InfoIconTypes.QUERY_TOOL} />
          <AdvancedSettingsButton tool={tool} />
        </Heading>
        {forceAgent || disableAgent ? null : (
          <Container top={16}>
            <AgentSelectWrapper>
              <select onChange={onAgentChange} value={agentName}>
                {agents.map((agent) => (
                  <option key={agent} value={agent}>
                    {agent}
                  </option>
                ))}
              </select>
            </AgentSelectWrapper>
          </Container>
        )}

        <Container>
          <ChatHistory chatHistoryData={chatHistoryData} tool={tool} disableAgent={disableAgent} />
          {chatHistoryData.length > 0 && (
            <Container top={16} bottom={16} row verticallyCenter spaceBetween>
              <div></div>
              <TransparentFlatButton onClick={onReset}>Clear Chat History</TransparentFlatButton>
            </Container>
          )}
        </Container>
        <Container top={36} bottom={16}>
          <InputAreaWrapper>
            <ChatInputsWrapper>
              <FormWrapper>
                <textarea name={'question'} placeholder="Ask a question" onChange={onQuestionChange} value={question} />
              </FormWrapper>
              <Container bottom={8}>
                <SubmitButton
                  onSubmit={isSubmittingQuestion ? () => null : handleSubmit}
                  Disabled={isSubmittingQuestion}
                />
              </Container>
            </ChatInputsWrapper>

            <Container row verticallyCenter spaceBetween style={{ width: '480px' }}>
              <SelectSourceDocumentsButtonWrapper>
                <TransparentFlatButton onClick={handleSelectSourceDocumentsClick}>
                  + Select Source Documents
                </TransparentFlatButton>
              </SelectSourceDocumentsButtonWrapper>
              {selectedDocs.length > 0 && (
                <RemoveAllButtonWrapper>
                  <TransparentFlatButton onClick={clearSelectedDocs}>Unselect All</TransparentFlatButton>
                </RemoveAllButtonWrapper>
              )}
            </Container>

            <Container top={16}>
              <SelectedDocuments
                selectedDocs={selectedDocs}
                onSelectedDocsChange={onSelectedDocsChange}
                showAddAllDocumentsAction={false}
                hideRemoveAllDocumentsAction={true}
              />
            </Container>
          </InputAreaWrapper>
        </Container>
      </Page>
    </>
  );
}
